import React from 'react'
import { Accordion, Card, Col, Row } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { RootState } from '../../store'
import paymentData, { setPaymentMethod } from '../../store/paymentData'
import { onlinePaymentTypeNames, OnlinePaymentTypes, PaymentConfigurations } from '../../store/program/interfaces'
import { DebitForm, DebitFormReadOnly } from './DebitForm'
import { AddPaymentValidatorFunc, RemovePaymentValidatorFunc, Validator } from '../../hooks/validators'

export type PaymentTypeProps = {
  selected: boolean
  addValidator: (trigger: Validator) => void
  showReadOnly: boolean
}

export type PaymentType = {
  name: string
  code: string
  description: (configs: PaymentConfigurations) => JSX.Element
  enabled: (configs: PaymentConfigurations) => boolean
  children?: (props: PaymentTypeProps) => JSX.Element
}

export const paymentTypes: PaymentType[] = [
  {
    name: 'Barzahlung',
    code: 'cash',
    description: (configs) =>
      configs.cash.notes_html ? (
        <p dangerouslySetInnerHTML={{ __html: configs.cash.notes_html }}></p>
      ) : (
        <p>
          Bezahlung in Bar. Weitere Informationen über Zeitpunkt und Ort der
          Zahlung finden sich in der Programmbeschreibung oder direkt bei der
          Institution.
        </p>
      ),
    enabled: (configs) => configs.cash.enabled,
  },
  {
    name: 'Lastschrift',
    code: 'debit',
    description: (_) => (
      <p>
        Bankdaten angeben und der Institution erlauben, die Beträge
        einzuziehen.
      </p>
    ),
    enabled: (configs) => configs.debit.enabled,
    children: DebitForm,
  },
  {
    name: 'Überweisung',
    code: 'transfer',
    description: (_) => (
      <p>Der Betrag muss auf das Konto der Institution überwiesen werden.</p>
    ),
    enabled: (configs) => configs.transfer.enabled,
  },
  {
    name: 'Online',
    code: 'online',
    description: (config) => (
      <>
        Nach der Zuteilung direkt online bezahlen mit <br />
        <ul className="list-inline">
          {Object.values(OnlinePaymentTypes)
            .filter((pType) => config.online[pType].enabled)
            .map(
              (pType) =>
                config.online[pType].methods || [onlinePaymentTypeNames[pType]]
            )
            .flat()
            .map((name, key) => (
              <li key={key}>&bull; {name}</li>
            ))}
        </ul>
      </>
    ),
    enabled: (configs) => configs.online.enabled,
  },
  {
    name: 'Auf Rechnung',
    code: 'invoice',
    description: (_) => (
      <p>
        Bezahlung auf Rechnung. Von den jeweiligen Veranstaltern oder
        der Institution wird eine Rechnung zugestellt.
      </p>
    ),
    enabled: (configs) => configs.invoice.enabled,
  },
  {
    name: 'Sonstiges',
    code: 'custom',
    description: (configs) => configs.custom.notes_html ? (
      <p dangerouslySetInnerHTML={{ __html: configs.custom.notes_html }}></p>
    ) : (
      <p>
        Es gelten gesonderte Bezahlbedingungen.
      </p>
    ),
    enabled: (configs) => configs.custom.enabled,
  },
]

const PaymentTypeSelector = (props: {
  paymentConfigs: PaymentConfigurations
  addValidator: AddPaymentValidatorFunc
  removeValidator: RemovePaymentValidatorFunc
}) => {
  const dispatch = useDispatch()
  const { method: selectedPaymentMethod } = useSelector(
    (state: RootState) => state.paymentData,
  )

  const { paymentConfigs, addValidator, removeValidator } = props
  const participantFromBackend = useSelector((state: RootState) => state.participant.participantFromBackend)

  let activePaymentTypes = paymentTypes.filter(pType =>
    pType.enabled(paymentConfigs),
  )

  const [debitSelected, setDebitSelected] = React.useState<boolean>(false)

  function OnHeaderClick(pType: PaymentType) {
    if (pType.code !== selectedPaymentMethod) {
      const debitChangeValue = selectedPaymentMethod === 'debit'
      if (debitSelected !== debitChangeValue) {
        if (!debitSelected)
          removeValidator(
            activePaymentTypes.findIndex(
              payType => payType.code === 'debit',
            ),
          )
        setDebitSelected(debitChangeValue)
      }
      dispatch(setPaymentMethod(pType.code))
    }
  }

  return (
    <Accordion>
      {activePaymentTypes.map((pType, key: number) => (
        <Card key={key}>
          <Card.Header
            onClick={() => {
              OnHeaderClick(pType)
            }}
            onKeyDown={(event: any) => {
              if (event.key === 'Enter' || event.key === ' ' || event.code === 'Space' || event.keyCode === 32) {
                OnHeaderClick(pType)
              }
            }}
            aria-expanded={
              selectedPaymentMethod === 'debit' &&
              pType.code === selectedPaymentMethod
            }
            tabIndex={0}
            className={`
              clickable
              ${
              pType.code === selectedPaymentMethod
                ? 'badge-success text-white'
                : undefined
            }`}
          >
            <FontAwesomeIcon
              icon={
                pType.code === selectedPaymentMethod
                  ? 'check-square'
                  : ['far', 'square']
              }
              aria-label={'Checkbox für die Bezahlmethode ' + pType.name}
            />{' '}
            {pType.name}
          </Card.Header>
          <Card.Body
            className={activePaymentTypes.length === 1 ? 'border-bottom' : ''}
          >
            {pType.description(paymentConfigs)}
            {pType.code === selectedPaymentMethod && pType.children ? (
              <>
                <br />
                <pType.children
                  showReadOnly={!!(pType.code === "debit" && participantFromBackend?.account_iban)}
                  addValidator={
                    (validator) =>
                    {
                      addValidator({
                          trigger: async () => await validator.trigger(),
                          formName: 'PaymentForm',
                        }, key,
                      )
                    }
                  }
                  selected={debitSelected}
                />
              </>
            ) : (
              <></>
            )}
          </Card.Body>
        </Card>
      ))}
    </Accordion>
  )
}

export default PaymentTypeSelector
